/** Artificial network conditions for local netcode testing. */
export interface NetSimConfig {
  /** Round-trip time in ms (split evenly between the two directions). */
  rttMs: number;
  /** +/- random jitter in ms added per direction. */
  jitterMs: number;
  /** Packet loss percentage (0–100), applied independently per direction. */
  lossPct: number;
}

export const NETSIM_OFF: NetSimConfig = { rttMs: 0, jitterMs: 0, lossPct: 0 };

/**
 * A WebSocket wrapper that (a) can inject latency, jitter and packet loss on
 * both directions — the single most important tool for catching prediction /
 * interpolation / reconciliation bugs locally — and (b) reconnects forever
 * with capped exponential backoff, so a dropped connection (server deploy,
 * laptop sleep, flaky wifi, idle timeout) heals without a page reload.
 */
export class Transport {
  connected = false;
  private ws: WebSocket | null = null;
  private sim: NetSimConfig;
  private retryMs = 500;
  private closedForGood = false;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;

  constructor(
    private readonly url: string,
    private readonly onMessage: (data: ArrayBuffer) => void,
    sim: NetSimConfig = NETSIM_OFF,
    private readonly onOpen?: () => void,
    /** Called once per disconnect with the WebSocket close code. */
    private readonly onClose?: (code: number) => void,
  ) {
    this.sim = sim;
    this.connect();
    // A woken/refocused tab shouldn't wait out a long backoff to come back.
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') this.retryNow();
    });
    window.addEventListener('online', () => this.retryNow());
  }

  private connect(): void {
    if (this.closedForGood) return;
    const ws = new WebSocket(this.url);
    this.ws = ws;
    ws.binaryType = 'arraybuffer';
    ws.addEventListener('open', () => {
      this.connected = true;
      this.retryMs = 500; // healthy again — reset the backoff
      this.onOpen?.();
    });
    ws.addEventListener('close', (ev) => {
      if (this.ws !== ws) return; // superseded by a newer socket
      const wasConnected = this.connected;
      this.connected = false;
      this.ws = null;
      if (wasConnected) this.onClose?.(ev.code);
      this.scheduleReconnect();
    });
    ws.addEventListener('error', () => {
      // 'close' always follows 'error'; reconnect is scheduled there.
      if (this.ws === ws) this.connected = false;
    });
    ws.addEventListener('message', (ev) => {
      this.withSim(() => this.onMessage(ev.data as ArrayBuffer));
    });
  }

  private scheduleReconnect(): void {
    if (this.closedForGood || this.reconnectTimer) return;
    const jitter = Math.random() * 0.3 + 0.85; // ±15% so clients don't stampede
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      this.connect();
    }, this.retryMs * jitter);
    this.retryMs = Math.min(this.retryMs * 2, 10_000);
  }

  /** Skip any pending backoff and try to reconnect immediately. */
  retryNow(): void {
    if (this.connected || this.closedForGood) return;
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    this.retryMs = 500;
    this.connect();
  }

  setSim(sim: NetSimConfig): void {
    this.sim = sim;
  }

  send(data: ArrayBuffer): void {
    this.withSim(() => {
      if (this.ws?.readyState === WebSocket.OPEN) this.ws.send(data);
    });
  }

  /** Send immediately, bypassing the latency sim — for reliable control messages. */
  sendNow(data: ArrayBuffer): void {
    if (this.ws?.readyState === WebSocket.OPEN) this.ws.send(data);
  }

  /** Run `fn` after the simulated one-way delay, or drop it (loss). */
  private withSim(fn: () => void): void {
    const { rttMs, jitterMs, lossPct } = this.sim;
    if (rttMs === 0 && jitterMs === 0 && lossPct === 0) {
      fn();
      return;
    }
    if (lossPct > 0 && Math.random() * 100 < lossPct) return; // dropped
    const delay = Math.max(0, rttMs / 2 + (Math.random() * 2 - 1) * jitterMs);
    setTimeout(fn, delay);
  }
}
